import { Box, Container, Typography, Grid, Link, IconButton, Stack, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { motion } from 'framer-motion';
import { companyName, address, gst, contacts, MAP_EMBED_SRC } from '../store/contactInfo';

export default function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'secondary.main',
        color: 'grey.300',
        pt: 6,
        pb: 3,
        borderTop: '6px solid',
        borderColor: 'primary.main',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Inventory2Icon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: '#fff',
                    letterSpacing: 0.5,
                    fontFamily: 'Bebas Neue, sans-serif',
                  }}
                >
                  {companyName}
                </Typography>
              </Box>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                Address
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.400', mb: 2, maxWidth: 360, lineHeight: 1.7 }}>
                {address.line1}
                <br />
                {address.line2}
                <br />
                {address.line3}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                GST
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.400', mb: 2 }}>
                {gst}
              </Typography>
              <Stack direction="row" spacing={1}>
                {[FacebookIcon, TwitterIcon, LinkedInIcon, InstagramIcon].map((Icon, i) => (
                  <IconButton
                    key={i}
                    size="small"
                    sx={{
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.05)',
                      '&:hover': { bgcolor: 'primary.main' },
                    }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#fff',
                  mb: 2,
                  fontWeight: 700,
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: 0.5,
                }}
              >
                Products &amp; pages
              </Typography>
              <Stack spacing={1}>
                {[
                  { label: 'Products', to: '/products' },
                  { label: 'Categories', to: '/categories' },
                  { label: 'Case studies', to: '/case-studies' },
                  { label: 'About', to: '/about' },
                  { label: 'Blog', to: '/blog' },
                  { label: 'Contact', to: '/contact' },
                ].map((l) => (
                  <Link
                    key={l.label}
                    component={RouterLink}
                    to={l.to}
                    color="inherit"
                    underline="none"
                    variant="body2"
                    sx={{ '&:hover': { color: 'primary.main', transition: 'all 0.2s' } }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#fff',
                  mb: 2,
                  fontWeight: 700,
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: 0.5,
                }}
              >
                Contact
              </Typography>
              <Stack spacing={2}>
                {contacts.map((person) => (
                  <Box key={person.email}>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                      {person.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.25 }} />
                      <Link
                        href={`tel:+91${person.phone}`}
                        underline="hover"
                        sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.95rem' }}
                      >
                        {person.phoneDisplay}
                      </Link>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.25 }} />
                      <Link href={`mailto:${person.email}`} underline="hover" sx={{ color: 'grey.300', fontSize: '0.9rem' }}>
                        {person.email}
                      </Link>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#fff',
                  mb: 2,
                  fontWeight: 700,
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: 0.5,
                }}
              >
                Find us
              </Typography>
              <Box
                sx={{
                  borderRadius: 0,
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.12)',
                  height: { xs: 220, sm: 260 },
                  maxWidth: '100%',
                }}
              >
                <Box
                  component="iframe"
                  title={`${companyName} on Google Maps`}
                  src={MAP_EMBED_SRC}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  sx={{
                    border: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mt: 4, mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="caption" sx={{ color: 'grey.600' }}>
            © {new Date().getFullYear()} {companyName}. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              href="#"
              color="inherit"
              underline="hover"
              variant="caption"
              sx={{ color: 'grey.600', '&:hover': { color: 'primary.main' } }}
            >
              Privacy Policy
            </Link>
            <Link
              href="#"
              color="inherit"
              underline="hover"
              variant="caption"
              sx={{ color: 'grey.600', '&:hover': { color: 'primary.main' } }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
